// Directe 1:1 poort van de bewezen camerawiskunde uit het wegwerpprototype
// (docs/sprekers-canvas-studio-prototype/index.html). Elke functie krijgt
// `stage: {w,h}` als expliciet argument i.p.v. een closed-over DOM-element
// (het prototype's `stageSize()`) -- dat houdt deze module puur en
// unit-testbaar zonder DOM.

export interface Box {
  x: number;
  y: number;
  width: number;
  height: number;
}

export interface Stage {
  w: number;
  h: number;
}

export interface Camera {
  scale: number;
  tx: number;
  ty: number;
}

export interface Point {
  x: number;
  y: number;
}

export function cameraFor(box: Box, stage: Stage): Camera {
  const scale = Math.min(stage.w / box.width, stage.h / box.height);
  const tx = stage.w / 2 - scale * (box.x + box.width / 2);
  const ty = stage.h / 2 - scale * (box.y + box.height / 2);
  return { scale, tx, ty };
}

// Bordcoördinaat waar een camerastand precies op gecentreerd staat -- het
// omgekeerde van cameraFor(). Nodig omdat tx/ty schermpixels zijn die AL met
// hun eigen schaal zijn vermenigvuldigd: bij twee standen met een sterk
// verschillende schaal (bv. het cirkeldetail t.o.v. het volledige overzicht)
// betekent lineair interpoleren van tx/ty zelf dat het tussenpunt niets meer
// met het bord te maken heeft. Door het BORDCOÖRDINAAT te interpoleren
// (i.p.v. de al-geschaalde pixelwaarde) blijft de camera altijd naar een
// zinnig punt op het bord kijken, ongeacht hoe ver de boog tussentijds
// uitzoomt -- fix voor de eerder gevonden "blanco scherm bij grote sprong"-bug.
export function centerOf(camera: Camera, stage: Stage): Point {
  return { x: (stage.w / 2 - camera.tx) / camera.scale, y: (stage.h / 2 - camera.ty) / camera.scale };
}

export function easeInOutCubic(t: number): number {
  return t < 0.5 ? 4 * t * t * t : 1 - (-2 * t + 2) ** 3 / 2;
}

export interface TravelAndScaleRatio {
  boardDistance: number;
  travelRatio: number;
  scaleRatio: number;
}

export function travelAndScaleRatio(src: Camera, dest: Camera, stage: Stage): TravelAndScaleRatio {
  const c0 = centerOf(src, stage);
  const c1 = centerOf(dest, stage);
  const boardDistance = Math.hypot(c1.x - c0.x, c1.y - c0.y);
  const viewportDiag = Math.hypot(stage.w, stage.h) || 1;
  const avgScale = Math.sqrt(src.scale * dest.scale);
  const avgFovDiag = viewportDiag / avgScale;
  return {
    boardDistance,
    travelRatio: boardDistance / avgFovDiag,
    scaleRatio: Math.max(dest.scale, src.scale) / Math.min(dest.scale, src.scale),
  };
}

export function computeDip(src: Camera, dest: Camera, stage: Stage): number {
  const r = travelAndScaleRatio(src, dest, stage);
  return Math.min(1.1, 0.55 * r.travelRatio + 0.3 * Math.log(r.scaleRatio));
}

// Afstandsafhankelijke duur: duration = clamp(650, distance * factor, 1400).
// "distance" is hier travelRatio (een schaal-onafhankelijke afstandsmaat)
// i.p.v. ruwe pixels, zodat een sprong tussen sterk verschillende
// zoomniveaus (bv. cirkeldetail -> overzicht) niet een absurd lange duur krijgt.
export function durationFor(src: Camera, dest: Camera, stage: Stage): number {
  const r = travelAndScaleRatio(src, dest, stage);
  const factor = 900;
  const raw = 650 + r.travelRatio * factor + Math.log(r.scaleRatio) * 220;
  return Math.max(650, Math.min(1400, raw));
}

export function cameraAtT(src: Camera, dest: Camera, dip: number, rawT: number, stage: Stage): Camera {
  const c0 = centerOf(src, stage);
  const c1 = centerOf(dest, stage);
  const t = easeInOutCubic(rawT);
  const hump = Math.sin(Math.PI * t); // 0 bij t=0/1, 1 op het hoogtepunt
  const logScale = Math.log(src.scale) + (Math.log(dest.scale) - Math.log(src.scale)) * t - dip * hump;
  const scale = Math.exp(logScale);
  const centerX = c0.x + (c1.x - c0.x) * t;
  const centerY = c0.y + (c1.y - c0.y) * t;
  return { scale, tx: stage.w / 2 - scale * centerX, ty: stage.h / 2 - scale * centerY };
}

// Gekantelde kaart (frame.rotation): de camera mikt op de omhullende
// rechthoek van de GEDRAAIDE vorm, niet op de rechte x/y/width/height --
// anders zoomt de camera exact op de ongekantelde afmeting in, en steken de
// gedraaide hoeken van de kaart zichtbaar buiten beeld (geconstateerd tijdens
// het testen van de kanteling op "closing" bij volledige focus in het
// prototype). Fix voor de eerder gevonden "afgeknipte hoeken bij gekantelde
// kaart"-bug.
export function rotatedBoundingBox(box: Box, rotationDeg: number): Box {
  const rad = (Math.abs(rotationDeg) * Math.PI) / 180;
  const w = box.width * Math.cos(rad) + box.height * Math.sin(rad);
  const h = box.width * Math.sin(rad) + box.height * Math.cos(rad);
  const cx = box.x + box.width / 2;
  const cy = box.y + box.height / 2;
  return { x: cx - w / 2, y: cy - h / 2, width: w, height: h };
}
